import React, { useEffect, useRef, useState } from "react";
import { Button, Container, Spinner } from "react-bootstrap";
import { FilesBlockProps } from "./files-block.props";
import WaveSurfer from "wavesurfer.js";
import { WaveSurferPlayer } from "./wave-surfer-player";
import { LazyMedia } from "./lazy-media";
import { downloadFile, FileItem, FileType, getFileType, useCachedFile } from "@/shared/lib";
import styles from "./files-block.module.css";
import { useMediaQuery } from "react-responsive";
import { TextFile } from "./tect-file";

export const FilesBlock = ({
  fileUrls,
  filesArr,
  filesErr,
  msgErr,
  filesFromUser,
  loadingMessage,
  isSelected,
  handleRightClick,
  openFileModal,
  hasText,
  hasTextTop,
}: FilesBlockProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const isMobile = useMediaQuery({ maxWidth: 507 });
  const scrollAmount = isMobile ? 350 : 750;
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);

  const objectUrls = useRef<Record<number, string>>({});
  const waveSurferInstances = useRef<Record<number, WaveSurfer | null>>({});

  useEffect(() => {
    return () => {
      Object.values(objectUrls.current).forEach((url) =>
        URL.revokeObjectURL(url)
      );
      Object.values(waveSurferInstances.current).forEach((ws) => ws?.destroy());
    };
  }, []);

  const checkScrollButtonsVisibility = () => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    setShowLeftButton(el.scrollLeft > 0);
    setShowRightButton(el.scrollLeft + el.clientWidth < el.scrollWidth);
  };

  useEffect(() => {
    checkScrollButtonsVisibility();
    const onResize = () => checkScrollButtonsVisibility();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [fileUrls, filesArr]);

  const onScrollLeft = (
    e: React.MouseEvent<HTMLButtonElement | undefined, MouseEvent>
  ) => {
    e.stopPropagation();
    e.preventDefault();
    scrollRef.current?.scrollBy({ left: -scrollAmount, behavior: "smooth" });
  };
  const onScrollRight = (
    e: React.MouseEvent<HTMLButtonElement | undefined, MouseEvent>
  ) => {
    e.stopPropagation();
    e.preventDefault();
    scrollRef.current?.scrollBy({ left: scrollAmount, behavior: "smooth" });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      const hasHorizontalScroll = el.scrollWidth > el.clientWidth;

      if (!hasHorizontalScroll) {
        return;
      }

      e.preventDefault();
      el.scrollBy({ left: e.deltaY });
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  const onScroll = () => checkScrollButtonsVisibility();

  // const anySelected = selectedIndices.size > 0;

  const handleRightClickHandler = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    type: FileType,
    file: FileItem
  ) => {
    e.stopPropagation();
    handleRightClick(e, type, file);
  };

  return (
    <Container
      style={{
        position: "relative",
        maxWidth: "100%",
        width: "100%",
        padding: "0",
        margin: "auto",
      }}
    >
      {showLeftButton && (
        <Button
          onClick={onScrollLeft}
          variant="dark"
          className={styles.scrollButton}
          style={{ left: -4 }}
          aria-label="Scroll left"
        >
          ‹
        </Button>
      )}

      <div
        ref={scrollRef}
        onScroll={onScroll}
        style={{
          userSelect: "none",
          overflowX: "auto",
          whiteSpace: "nowrap",
          scrollbarWidth: "none",
          padding: 0,
          position: "relative",
          maxHeight: 250,
        }}
      // className="hide-scrollbar rounded-5"
      >
        {loadingMessage && (
          <div
            style={{ zIndex: 1201 }}
            className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
          >
            <Spinner animation="border" size="sm" role="status" />
          </div>
        )}

        {filesArr.map((file, idx) => {
          const type = getFileType(file);
          
          // const isSelected = selectedIndices.has(idx);
          // const fileName =
          //   typeof file === "string" ? file.split("/").pop()! : file.name;
          // const hasError = filesErr?.includes(fileName);

          let content;

          if (type === "image") {
            content = (
              <LazyMedia
                src={file}
                alt={`Image ${idx}`}
                type="image"
                isSelected={isSelected}
                rounded={
                  hasText ? "rounded-top-5 rounded-bottom-0" :
                    hasTextTop ? "rounded-top-0 rounded-bottom-5" :
                      "rounded-5"
                }
                // style={{
                //   filter: isSelected ? "brightness(0.8)" : undefined,
                // }}
                handleRightClick={(e) => handleRightClickHandler(e, type, file)}
                onClick={(url) => {
                  if (url.startsWith("blob:")) {
                    openFileModal(fileUrls[idx]);
                  } else {
                    openFileModal(url);
                  }
                }}
              />
            );
          } else if (type === "video") {
            content = (
              <LazyMedia
                src={file}
                alt={`video ${idx}`}
                type="video"
                isSelected={isSelected}
                rounded={
                  hasText ? "rounded-top-5 rounded-bottom-0" :
                    hasTextTop ? "rounded-top-0 rounded-bottom-5" :
                      "rounded-5"
                }
                // style={{
                //   filter: isSelected ? "brightness(0.8)" : undefined,
                // }}
                handleRightClick={(e) => handleRightClickHandler(e, type, file)}
                // onClick={(url) => openFileModal(url)}
                onClick={(url) => {
                  if (url.startsWith("blob:")) {
                    openFileModal(fileUrls[idx]);
                  } else {
                    openFileModal(url);
                  }
                }}
              />
            );
          } else if (type === "audio") {
            content = (
              <div
                style={{
                  width: "100%",
                  maxWidth: "300px",
                  display: "flex",
                  padding: "2px 10px 2px 4px",
                  alignItems: "center",
                  background: isSelected
                    ? "#3284ff"
                    :
                    !filesFromUser
                      ? "#6c757d"
                      : "#f8f9fa",
                  borderRadius: "2rem",
                  // filter: isSelected ? "brightness(0.85)" : undefined,
                }}
                onContextMenu={(e) => handleRightClickHandler(e, type, file)}
              >
                <WaveSurferPlayer src={file} isSelected={isSelected} isUser={filesFromUser} />
              </div>
            );
          } else if (type === "text") {
            content = (
              <TextFile
                file={file}
                isSelected={isSelected}
                  onClick={(url) => {
                  if (url.startsWith("blob:")) {
                    openFileModal(fileUrls[idx]);
                  } else {
                    openFileModal(url);
                  }
                }}
              />
            );
          } else {
            const ext =
              typeof file === "string"
                ? file.split(".").pop()?.toUpperCase() || "FILE"
                : file.name.split(".").pop()?.toUpperCase() || "FILE";

            content = (
              <div
                onContextMenu={(e) => handleRightClickHandler(e, type, file)}
                className={styles.fileTypeShortBox}
                // style={{ filter: isSelected ? "brightness(0.9)" : undefined }}
                title={ext}
              // onClick={(url) => openFileModal(url, "image")}
              >
                {ext}
              </div>
            );
          }




          return (
            <span
              key={idx}
              style={{
                display: "inline-block",
                marginRight: idx === fileUrls.length - 1 ? 0 : ".45rem",
                position: "relative",
              }}
            >
              {content}

              {/* {hasError && (
                <div
                  style={{
                    position: "absolute",
                    bottom: 11,
                    right: filesFromUser ? 18 : 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#dc3545",
                    color: "white",
                    fontSize: "10px",
                    zIndex: 1,
                  }}
                >
                  <ExclamationCircle size={4} />
                </div>
              )} */}
            </span>
          );
        })}
      </div>

      {showRightButton && (
        <Button
          onClick={onScrollRight}
          variant="dark"
          // size="sm"
          className={styles.scrollButton}
          style={{ right: -4 }}
          aria-label="Scroll right"
        >
          ›
        </Button>
      )}
    </Container>
  );
};
